import { createSelector } from '@reduxjs/toolkit';
import { StoreType } from 'store';
import { ActionResult, DialogPayload, Status, VisibleGroup } from './types';
import { ConsentHubGroup, ItemStatus } from '../../services/api';

export const selectEmail = createSelector(
  (state: StoreType) => state.preferences.email,
  (email: string | undefined) => email,
);

export const selectGroup = createSelector(
  (state: StoreType, groupKey: string) => state.preferences.groups[groupKey],
  (group: ConsentHubGroup) => group,
);

export const selectItemsByGroupAndDeployment = (groupKey: string, tabKey: string) =>
  createSelector(
    (state: StoreType) => state.preferences.groups,
    (groups: Record<string, ConsentHubGroup>) => {
      return Object.keys(groups[groupKey].tabs[tabKey].items);
    },
  );

export const selectGroupKeys = createSelector(
  (state: StoreType) => state.preferences.groups,
  (state: StoreType) => state.preferences.filter,
  (groups: Record<string, ConsentHubGroup>, filter: VisibleGroup) => {
    let groupKeys = Object.keys(groups);

    if (filter.groupKey !== 'all') {
      groupKeys = groupKeys.filter((key) => key === filter.groupKey);
    }

    return groupKeys;
  },
);

export const selectVisibleGroup = createSelector(
  (state: StoreType) => state.preferences.filter,
  (filter: VisibleGroup) => filter,
);

export const selectIsAllSubscriptionGroupForView = createSelector(
  (state: StoreType) => state.preferences.filter,
  (subscriptionGroupForView: VisibleGroup) => subscriptionGroupForView.groupKey === 'all',
);

export const selectItem = (groupKey: string, itemKey: string, tabKey?: string) =>
  createSelector(
    (state: StoreType) => state.preferences.groups,
    (groups: Record<string, ConsentHubGroup>) => {
      try {
        return groups[groupKey].tabs[tabKey ?? 'root'].items[itemKey];
      } catch {
        return undefined;
      }
    },
  );

export const selectItemInitial = (itemKey: string) =>
  createSelector(
    (state: StoreType) => state.preferences.initialStates,
    (initialStates: Record<string, ItemStatus | undefined>) => initialStates[itemKey],
  );

export const selectActionResult = createSelector(
  (state: StoreType) => state.preferences.actionResult,
  (actionResult: ActionResult | undefined) => actionResult,
);

export const selectPreferencesStatus = createSelector(
  (state: StoreType) => state.preferences.status,
  (status: Status) => status,
);

export const selectConsentDialog = createSelector(
  (state: StoreType) => state.preferences.consentDialog,
  (consentDialog: DialogPayload) => consentDialog,
);

export const selectConsentDeclinePrDialog = createSelector(
  (state: StoreType) => state.preferences.consentDeclinePrDialog,
  (consentDeclinePrDialog: DialogPayload) => consentDeclinePrDialog,
);

export const selectGlobalUnsubscribeDialog = createSelector(
  (state: StoreType) => state.preferences.globalUnsubscribeDialog,
  (globalUnsubscribeDialog: DialogPayload) => globalUnsubscribeDialog,
);

export const selectDoubleOptInDialog = createSelector(
  (state: StoreType) => state.preferences.doubleOptInDialog,
  (doubleOptInDialog: DialogPayload) => doubleOptInDialog,
);

export const selectHid = createSelector(
  (state: StoreType) => state.preferences?.hid,
  (hid: string | undefined) => hid,
);
